import { requestZeusReadFromBrowser } from 'components/api/zeusBrowserClient';
import {
  MarketplaceMarketRouteDataDocument,
  MarketplaceZoneRouteDataDocument,
} from 'components/marketplace/browse/__generated__/marketplace-browse.zeus';

import { buildMarketInfoFromMarket } from '../helpers/marketInfo';
import { normalizeSearchVertices } from '../helpers/useSearchQueries';
import type { GeoPoint, MarketInfo } from '../map/types';
import { getZoneFrameBounds } from '../map/utils';
import type { SavedSearchFieldsFragment } from './__generated__/saved-searches.zeus';
import { isCanonicalMarketSlug, pickPathname, pickRouteMarket } from './routing';

/** Where a saved search applies on the map page. */
export type SavedSearchArea = {
  pathname: string;
  /** The route's market info, as server rendering builds it for `pathname`. */
  marketInfo: MarketInfo;
  /**
   * The market the saved search stores. A zone route has no market identifier,
   * but the save button must still compare the saved market.
   */
  saveMarket: string | null;
};

/** The rectangle around every point, as a closed ring. */
const envelopeRing = (rings: GeoPoint[][]): GeoPoint[] => {
  const points = rings.flat();
  const lats = points.map((point) => point.lat);
  const lons = points.map((point) => point.lon);
  const north = Math.max(...lats);
  const south = Math.min(...lats);
  const east = Math.max(...lons);
  const west = Math.min(...lons);
  return [
    { lat: north, lon: west },
    { lat: north, lon: east },
    { lat: south, lon: east },
    { lat: south, lon: west },
    { lat: north, lon: west },
  ];
};

const fetchZoneArea = async (zoneId: string, signal: AbortSignal) => {
  const data = await requestZeusReadFromBrowser(
    MarketplaceZoneRouteDataDocument,
    { zoneId },
    signal,
  );
  const zone = data.marketplace.zone;
  // The same frame server rendering shows for the zone's route.
  const bounds = zone ? getZoneFrameBounds(zone) : null;
  if (!zone || !bounds) return null;
  const [[west, south], [east, north]] = bounds;
  return {
    name: zone.name,
    vertices: envelopeRing([
      [
        { lat: south, lon: west },
        { lat: north, lon: east },
      ],
    ]),
  };
};

const fetchMarket = async (marketSlug: string, signal: AbortSignal) => {
  const data = await requestZeusReadFromBrowser(
    MarketplaceMarketRouteDataDocument,
    { marketIdentifier: marketSlug },
    signal,
  );
  const market = data.marketplace.market;
  // An inactive market redirects during server rendering; a page load handles it.
  return market?.active ? market : null;
};

/**
 * Finds the route and the area of a saved search with the same data server
 * rendering uses, so the map page can apply it without loading a new page.
 * The labels follow server rendering for the same route. Returns null when
 * the area or its market is unknown; the caller then loads the page.
 */
export const resolveSavedSearchArea = async (
  savedSearch: SavedSearchFieldsFragment,
  signal: AbortSignal,
): Promise<SavedSearchArea | null> => {
  const { market, zoneIds, vertices } = savedSearch.location;
  const saveMarket = isCanonicalMarketSlug(market) ? market : null;
  const pathname = pickPathname(savedSearch);
  // The market server rendering uses for `pathname`, also for a search saved
  // without a market slug.
  const routeMarket = pickRouteMarket(savedSearch);

  // The same precedence as the search: a drawn area, then zones, then the market.
  const savedVertices = normalizeSearchVertices(vertices);
  if (savedVertices) {
    const found = await fetchMarket(routeMarket, signal);
    if (!found) return null;
    return {
      pathname,
      saveMarket,
      marketInfo: {
        displayName: found.displayName,
        identifier: found.identifier,
        name: found.identifier,
        shape: { coordinates: [savedVertices], type: 'Polygon' },
      },
    };
  }

  if (zoneIds.length === 1) {
    const zone = await fetchZoneArea(zoneIds[0], signal);
    if (!zone) return null;
    return {
      pathname,
      saveMarket,
      marketInfo: {
        displayName: zone.name,
        identifier: null,
        name: zone.name,
        shape: { coordinates: [zone.vertices], type: 'Polygon' },
      },
    };
  }

  const [found, ...zones] = await Promise.all([
    fetchMarket(routeMarket, signal),
    ...zoneIds.map((zoneId) => fetchZoneArea(zoneId, signal)),
  ]);
  if (!found) return null;
  const marketInfo = buildMarketInfoFromMarket(found, {});
  if (!marketInfo) return null;
  if (zones.length === 0) {
    return { pathname: `/homes/${found.identifier}`, saveMarket, marketInfo };
  }
  // A multi-zone search is a market route that searches the zones; the camera shows them all.
  const zoneAreas = zones.filter((zone) => zone != null);
  if (zoneAreas.length < zones.length) return null;
  return {
    pathname,
    saveMarket,
    marketInfo: {
      ...marketInfo,
      shape: {
        coordinates: [envelopeRing(zoneAreas.map((zone) => zone.vertices))],
        type: 'Polygon',
      },
    },
  };
};
